import { Head, usePage } from "@inertiajs/react"
import { CircleCheck, Clock } from "lucide-react"
import { UserLayout } from "@/components/layouts/user-layout"
import { ScreenHeader } from "@/components/ui/headers"
import { formatDateTime } from "@/lib/formatters"
import type { SelfInvoiceResult } from "@/features/user/types"
import { t } from "@/lib/i18n"

type Props = {
  invoice: SelfInvoiceResult
}

/**
 * What the diner gets back after asking, and what they see if they open the
 * same link again from the browser that asked.
 *
 * **Stamping is asynchronous**, so `pending` is the normal first answer rather
 * than an error: `Location::InvoiceStampJob` talks to the PAC and the row is
 * created before it. `failed` is deliberately shown with the same calm framing —
 * the retry ledger lives on the row and the sweep comes back for it, so from
 * the diner's side the only true statement is still "we are working on it".
 * Telling them a SAT code they cannot act on would only send them to a
 * restaurant that also cannot act on it yet.
 */
export default function UserInvoicesIssued() {
  const { invoice } = usePage<Props>().props
  const stamped = invoice.status === "stamped"

  return (
    <UserLayout locationName={invoice.locationName}>
      <Head title={t("ui.react.pages.user.invoices.issued.title")} />

      <div data-testid="self-invoice-issued-view">
        <ScreenHeader title={t("ui.react.pages.user.invoices.issued.title")} />

        <div className="w-full px-4 pb-10 pt-4">
          <div className="rounded-2xl border bg-card p-5">
            <div className="flex items-start gap-3">
              {/* The dot rule: colour lives in the mark, not in the sentence. */}
              {stamped ? (
                <CircleCheck className="mt-0.5 size-5 shrink-0 text-success" />
              ) : (
                <Clock className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-semibold" data-testid="self-invoice-status">
                  {invoice.statusLabel}
                </p>
                <p className="text-sm text-muted-foreground">
                  {stamped
                    ? t("ui.react.pages.user.invoices.issued.stamped_body")
                    : t("ui.react.pages.user.invoices.issued.pending_body")}
                </p>
              </div>
            </div>

            <dl className="mt-4 space-y-2 border-t pt-4 text-sm">
              {invoice.uuid && (
                <div className="flex flex-col gap-0.5" data-testid="self-invoice-uuid">
                  <dt className="text-xs text-muted-foreground">
                    {t("ui.react.pages.user.invoices.issued.folio")}
                  </dt>
                  {/* Not truncated: a UUID the diner has to give an accountant is
                      worth a wrapped line, and `break-all` is what keeps a
                      36-character string inside a phone's gutters. */}
                  <dd className="break-all font-medium tabular-nums">{invoice.uuid}</dd>
                </div>
              )}

              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">
                  {t("ui.react.pages.user.invoices.issued.rfc")}
                </dt>
                <dd className="text-right font-medium" data-testid="self-invoice-receiver-rfc">
                  {invoice.receiverRfc}
                </dd>
              </div>

              <div className="flex justify-between gap-3">
                <dt className="shrink-0 text-muted-foreground">
                  {t("ui.react.pages.user.invoices.issued.legal_name")}
                </dt>
                <dd className="min-w-0 text-right font-medium">{invoice.receiverLegalName}</dd>
              </div>

              {invoice.stampedAt && (
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">
                    {t("ui.react.pages.user.invoices.issued.stamped_at")}
                  </dt>
                  <dd className="text-right tabular-nums">
                    {formatDateTime(invoice.stampedAt, { timeZone: invoice.timeZone })}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Where the document itself comes from. Menny does not email it and
              does not host a download yet, so saying so is better than letting a
              diner wait for a mail that is not coming. */}
          <p className="mt-4 text-xs text-muted-foreground">
            {t("ui.react.pages.user.invoices.issued.delivery_note")}
          </p>
        </div>
      </div>
    </UserLayout>
  )
}
